import SwitchCard from "../../ui/SwitchCard/SwitchCard";
import ConfirmationModal from "../../ui/Modal/ConfirmationModal";
import styles from "./GenerateModal.module.scss";
import Button from "../../ui/Button/Button";
import { Repeat } from "../../ui/Icon/Icon";
import Collapsible from "../../ui/Collapsible/Collapsible";
import { z } from "zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import Switch from "../../ui/Switch/Switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";

const schema = z.object({
  regenerate: z.boolean(),
  regenerateBrief: z.boolean(),
  regenerateOutline: z.boolean(),
  regenerateContent: z.boolean(),
  regenerateLinks: z.boolean(),
});

export type GenerateArticleModalFormData = z.infer<typeof schema>;

function GenerateModal({
  onClose,
  fetching,
  onChange,
  onSubmit,
  generationOptions,
  articleCounts,
  children,
  confirmationText,
  defaultValues,
  creditsRequired,
}: {
  fetching: boolean;
  onClose: () => void;
  onChange?: (data: Partial<GenerateArticleModalFormData>) => void;
  onSubmit: (data: GenerateArticleModalFormData) => void;
  generationOptions?: {
    open?: boolean;
    hideBrief?: boolean;
    hideOutline?: boolean;
    hideContent?: boolean;
    hideLinks?: boolean;
  };
  articleCounts?: {
    total: number;
    generated: number;
    processing: number;
    published: number;
    draft: number;
  };
  children?: React.ReactNode;
  confirmationText?: string;
  defaultValues?: GenerateArticleModalFormData;
  creditsRequired: number;
}) {
  const regenerateCount = articleCounts
    ? articleCounts.generated + articleCounts.published
    : 0;
  const draftCount = articleCounts ? articleCounts.draft : 0;

  const { control, handleSubmit, setValue, getValues } =
    useForm<GenerateArticleModalFormData>({
      defaultValues: defaultValues ?? {
        regenerate: true,
        regenerateBrief: false,
        regenerateOutline: false,
        regenerateContent: true,
        regenerateLinks: true,
      },
      resolver: zodResolver(schema),
    });

  const values = useWatch({ control });

  useEffect(() => {
    if (onChange) {
      onChange(values);
    }
  }, [onChange, values]);

  const internalOnSubmit = (data: GenerateArticleModalFormData) => {
    onSubmit(data);
  };

  if (articleCounts && articleCounts.total - articleCounts.processing < 1) {
    return (
      <Button variant="primary" onClick={onClose} className={styles.ok}>
        Okay
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit(internalOnSubmit)}>
      {children}
      {articleCounts &&
        regenerateCount > 0 &&
        regenerateCount !== articleCounts.total && (
          <Controller
            control={control}
            name="regenerate"
            render={({ field }) => (
              <SwitchCard
                className={styles.card}
                IconComponent={Repeat}
                checked={field.value}
                onToggle={field.onChange}
                title={`Regenerate ${regenerateCount} ${
                  regenerateCount === 1 ? "article" : "articles"
                }`}
              />
            )}
          />
        )}

      <Collapsible
        defaultValue={!!generationOptions?.open}
        className={styles.regenerationOptions}
        title={`${draftCount > 0 ? "Generation" : "Regeneration"} options`}
      >
        <div className={styles.switches}>
          {!generationOptions?.hideBrief && (
            <Controller
              control={control}
              name="regenerateBrief"
              render={({ field }) => (
                <label htmlFor={field.name} className={styles.switch}>
                  <Switch
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);

                      const [
                        regenerateOutline,
                        regenerateContent,
                        regenerateLinks,
                      ] = getValues([
                        "regenerateOutline",
                        "regenerateContent",
                        "regenerateLinks",
                      ]);

                      if (value) {
                        if (!regenerateOutline) {
                          if (regenerateContent) {
                            setValue("regenerateOutline", true);
                          }

                          if (regenerateLinks) {
                            setValue("regenerateOutline", true);
                            setValue("regenerateContent", true);
                          }
                        }
                      }
                    }}
                  />
                  <span className={styles.switchLabel}>
                    {draftCount > 0 ? "Generate" : "Regenerate"} brief
                  </span>
                </label>
              )}
            />
          )}
          {!generationOptions?.hideOutline && (
            <Controller
              control={control}
              name="regenerateOutline"
              render={({ field }) => (
                <label htmlFor={field.name} className={styles.switch}>
                  <Switch
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);

                      const [
                        regenerateBrief,
                        regenerateContent,
                        regenerateLinks,
                      ] = getValues([
                        "regenerateBrief",
                        "regenerateContent",
                        "regenerateLinks",
                      ]);

                      if (!value && regenerateBrief && regenerateContent) {
                        setValue("regenerateContent", false);
                        setValue("regenerateLinks", false);
                      }

                      if (value && regenerateLinks) {
                        setValue("regenerateContent", true);
                      }
                    }}
                  />
                  <span className={styles.switchLabel}>
                    {draftCount > 0 ? "Generate" : "Regenerate"} outline
                  </span>
                </label>
              )}
            />
          )}
          {!generationOptions?.hideContent && (
            <>
              <Controller
                control={control}
                name="regenerateContent"
                render={({ field }) => (
                  <label htmlFor={field.name} className={styles.switch}>
                    <Switch
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value);

                        const [regenerateBrief, regenerateOutline] = getValues([
                          "regenerateBrief",
                          "regenerateOutline",
                        ]);

                        if (value) {
                          setValue("regenerateLinks", true);

                          if (regenerateBrief && !regenerateOutline) {
                            setValue("regenerateOutline", true);
                          }
                        }

                        if (!value && regenerateOutline) {
                          setValue("regenerateLinks", false);
                        }
                      }}
                    />
                    <span className={styles.switchLabel}>
                      {draftCount > 0 ? "Generate" : "Regenerate"} content
                    </span>
                  </label>
                )}
              />
              {!generationOptions?.hideLinks && (
                <Controller
                  control={control}
                  name="regenerateLinks"
                  render={({ field }) => (
                    <label htmlFor={field.name} className={styles.switch}>
                      <Switch
                        id={field.name}
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);

                          const [
                            regenerateBrief,
                            regenerateOutline,
                            regenerateContent,
                          ] = getValues([
                            "regenerateBrief",
                            "regenerateOutline",
                            "regenerateContent",
                          ]);

                          if (!value && regenerateContent) {
                            setValue("regenerateContent", false);
                          }

                          if (
                            value &&
                            regenerateBrief &&
                            !(regenerateOutline || regenerateContent)
                          ) {
                            setValue("regenerateOutline", true);
                            setValue("regenerateContent", true);
                          }

                          if (
                            value &&
                            regenerateOutline &&
                            !regenerateContent
                          ) {
                            setValue("regenerateContent", true);
                          }
                        }}
                      />
                      <span className={styles.switchLabel}>
                        {draftCount > 0 ? "Generate" : "Regenerate"} links
                      </span>
                    </label>
                  )}
                />
              )}
            </>
          )}
        </div>
      </Collapsible>
      <ConfirmationModal
        loading={fetching}
        onClose={onClose}
        confirmationText={
          confirmationText
            ? confirmationText
            : regenerateCount > 0
              ? `Regenerate (${
                  creditsRequired === 0
                    ? "Free"
                    : `${creditsRequired} ${
                        creditsRequired === 1 ? "credit" : "credits"
                      }`
                })`
              : `Generate (${
                  creditsRequired === 0
                    ? "Free"
                    : `${creditsRequired} ${
                        creditsRequired === 1 ? "credit" : "credits"
                      }`
                })`
        }
      />
    </form>
  );
}

export default GenerateModal;
